import React, { FC, useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { Add, Save, Close, Work } from '@mui/icons-material';
// Components
import { Toast, Modal, Loader } from '../../../components';
import {
  Card,
  Fade,
  Button,
  CardContent,
  CardHeader,
  TextField,
  Grid,
  Paper,
  ButtonGroup
} from '@mui/material';
// models
import { IAttorneyOffice } from '../../../models';
// fetch
import { updateAdminAttorneyOffice } from '../../../fetch';

interface IAttorneyOfficeModal {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  currentAttorneyOffice: IAttorneyOffice | null;
}

const AttorneyOfficeSchema = Yup.object().shape({
  workplace: Yup.string().max(255, 'Max 255 characters').required('Required'),
  contactFirstName: Yup.string().max(255, 'Max 255 characters'),
  contactMiddleName: Yup.string().max(255, 'Max 255 characters'),
  contactLastName: Yup.string().max(255, 'Max 255 characters'),
  address1: Yup.string().max(255, 'Max 255 characters'),
  address2: Yup.string().max(255, 'Max 255 characters'),
  city: Yup.string().max(255, 'Max 255 characters'),
  state: Yup.string().max(255, 'Max 255 characters'),
  zip: Yup.string(),
  phone: Yup.string().max(255, 'Max 255 characters'),
  fax: Yup.string().max(255, 'Max 255 characters')
});

export const AttorneyOfficeModal: FC<IAttorneyOfficeModal> = ({
  open,
  onClose,
  onSave,
  currentAttorneyOffice
}) => {
  const classes = useStyles();
  const [isError, showError] = useState<boolean>(false);
  const [isSuccess, showSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          workplace:
            currentAttorneyOffice && currentAttorneyOffice.workplace
              ? currentAttorneyOffice.workplace
              : '',
          contactFirstName:
            currentAttorneyOffice && currentAttorneyOffice.contactFirstName
              ? currentAttorneyOffice.contactFirstName
              : '',
          contactMiddleName:
            currentAttorneyOffice && currentAttorneyOffice.contactMiddleName
              ? currentAttorneyOffice.contactMiddleName
              : '',
          contactLastName:
            currentAttorneyOffice && currentAttorneyOffice.contactLastName
              ? currentAttorneyOffice.contactLastName
              : '',
          address1:
            currentAttorneyOffice && currentAttorneyOffice.address1
              ? currentAttorneyOffice.address1
              : '',
          address2:
            currentAttorneyOffice && currentAttorneyOffice.address2
              ? currentAttorneyOffice.address2
              : '',
          city:
            currentAttorneyOffice && currentAttorneyOffice.city ? currentAttorneyOffice.city : '',
          state:
            currentAttorneyOffice && currentAttorneyOffice.state ? currentAttorneyOffice.state : '',
          zip: currentAttorneyOffice && currentAttorneyOffice.zip ? currentAttorneyOffice.zip : '',
          phone: currentAttorneyOffice ? currentAttorneyOffice.phone : '',
          fax: currentAttorneyOffice && currentAttorneyOffice.fax ? currentAttorneyOffice.fax : ''
        }}
        validationSchema={AttorneyOfficeSchema}
        onSubmit={async (values, actions) => {
          try {
            await updateAdminAttorneyOffice({
              ...values,
              attorneyOfficeId: currentAttorneyOffice
                ? currentAttorneyOffice.attorneyOfficeId
                : undefined
            });

            setSuccessMessage('Success!');
            showSuccess(true);
            onClose();
            onSave();
            actions.resetForm();
          } catch (error) {
            console.log(error);
            if (
              error &&
              error.Errors &&
              Object.values(error.Errors)[0] &&
              Object.values(Object.values(error.Errors)[0])[0]
            ) {
              setErrorMessage(Object.values(Object.values(error.Errors)[0])[0] as string);
            }
            showError(true);
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  onClose();
                  resetForm();
                }
              }}
              maxWidth="md"
              title={currentAttorneyOffice ? 'EDIT ATTORNEY OFFICE' : 'ADD NEW ATTORNEY OFFICE'}
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <div className={classes.content}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Card elevation={0} variant="outlined" className={classes.marginBottom}>
                          <CardHeader
                            avatar={<Work />}
                            title="INFO"
                            className={classes.primaryHeader}
                          />
                          <CardContent className={classes.cardContent}>
                            <Paper elevation={0} square>
                              <div className={classes.column}>
                                <Grid container spacing={1}>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      required
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Workplace"
                                      name="workplace"
                                      value={values.workplace}
                                      onBlur={handleBlur}
                                      size="small"
                                      onChange={e => setFieldValue('workplace', e.target.value)}
                                      error={Boolean(touched.workplace && errors.workplace)}
                                      helperText={touched.workplace && errors.workplace}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Contact First Name"
                                      name="contactFirstName"
                                      size="small"
                                      value={values.contactFirstName}
                                      onBlur={handleBlur}
                                      onChange={e =>
                                        setFieldValue('contactFirstName', e.target.value)
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Contact Middle Name"
                                      name="contactMiddleName"
                                      size="small"
                                      value={values.contactMiddleName}
                                      onBlur={handleBlur}
                                      onChange={e =>
                                        setFieldValue('contactMiddleName', e.target.value)
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Contact Last Name"
                                      name="contactLastName"
                                      size="small"
                                      value={values.contactLastName}
                                      onBlur={handleBlur}
                                      onChange={e =>
                                        setFieldValue('contactLastName', e.target.value)
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Address"
                                      name="address1"
                                      size="small"
                                      value={values.address1}
                                      onBlur={handleBlur}
                                      onChange={e => setFieldValue('address1', e.target.value)}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="City"
                                      name="city"
                                      size="small"
                                      value={values.city}
                                      onBlur={handleBlur}
                                      onChange={e => setFieldValue('city', e.target.value)}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="State"
                                      name="state"
                                      size="small"
                                      value={values.state}
                                      onBlur={handleBlur}
                                      onChange={e => setFieldValue('state', e.target.value)}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="ZIP"
                                      name="zip"
                                      size="small"
                                      value={values.zip}
                                      onBlur={handleBlur}
                                      onChange={e => setFieldValue('zip', e.target.value)}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Phone Number"
                                      name="phone"
                                      size="small"
                                      value={values.phone}
                                      onBlur={handleBlur}
                                      onChange={e => setFieldValue('phone', e.target.value)}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Fax"
                                      name="fax"
                                      size="small"
                                      value={values.fax}
                                      onBlur={handleBlur}
                                      onChange={e => setFieldValue('fax', e.target.value)}
                                    />
                                  </Grid>
                                </Grid>
                              </div>
                            </Paper>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </div>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={currentAttorneyOffice ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      {currentAttorneyOffice ? 'Save' : 'Add Attorney Office'}
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        if (!deepEqual(initialValues, values)) {
                          const result = window.confirm(
                            'You have unsaved changes, are you sure you want to exit?'
                          );
                          if (result) {
                            resetForm();
                            onClose();
                          } else {
                            return;
                          }
                        } else {
                          onClose();
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
      <Toast
        id="attorney-success"
        message={successMessage || `Attorney Office Added!`}
        open={isSuccess}
        onClose={() => showSuccess(false)}
        variant="success"
      />
      <Toast
        id="attorney-error"
        autoHideDuration={5000}
        message={
          errorMessage ||
          'We were unable to create the attorney office at this time. Please try again later.'
        }
        open={isError}
        onClose={() => showError(false)}
        variant="error"
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    '& > div:not(:first-of-type)': {
      marginTop: theme.spacing(1)
    }
  },
  outlinedLabel: {
    backgroundColor: theme.palette.common.white,
    paddingLeft: 2,
    paddingRight: 2
  },
  content: {
    marginTop: theme.spacing(1)
  },
  cardContent: {
    paddingTop: 0
  },
  paperBorder: {
    border: `1px solid ${theme.palette.grey[300]}`
  }
}));
